// Modified from https://github.com/palmerhq/radio-group
import { useEffect, useCallback, useContext, useRef, PropsWithChildren } from 'react';

import keyCodes from '../../helpers/keyCodes';
import { RadioGroupContext, RadioProps } from './shared';

const Radio = ({ children, ...props }: PropsWithChildren<RadioProps<unknown>>) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const ctx = useContext(RadioGroupContext);
  const { groupId, otherRadioValues, setChecked, value } = ctx;
  const index = otherRadioValues.findIndex(i => i === props.value);
  const count = otherRadioValues.length - 1;
  const { onBlur, onFocus } = props;

  useEffect(() => {
    if (value === props.value) {
      // Don't hijack focus unless the currently focused element is part of the same radio group
      const activeElement = document.activeElement as HTMLElement;
      if (!activeElement.dataset || activeElement.dataset.radioGroupId !== groupId) {
        return;
      }

      if (ref.current !== null) {
        ref.current.focus();
      }
    }
  }, [groupId, value, props.value]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      event.persist();
      let flag = false;
      function setPrevious() {
        if (index === 0) {
          setChecked(otherRadioValues[count]);
        } else {
          setChecked(otherRadioValues[index - 1]);
        }
      }

      function setNext() {
        if (index === count) {
          setChecked(otherRadioValues[0]);
        } else {
          setChecked(otherRadioValues[index + 1]);
        }
      }

      switch (event.key) {
        case keyCodes.SPACE:
        case keyCodes.RETURN: {
          setChecked(props.value);
          flag = true;
          break;
        }
        case keyCodes.UP:
        case keyCodes.LEFT: {
          setPrevious();
          flag = true;
          break;
        }
        case keyCodes.DOWN:
        case keyCodes.RIGHT: {
          setNext();
          flag = true;
          break;
        }
        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    },
    [count, index, otherRadioValues, props.value, setChecked]
  );

  const handleClick = useCallback(() => {
    setChecked(props.value);
  }, [props.value, setChecked]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      onFocus?.(e);
    },
    [onFocus]
  );

  // Sets tabIndex="0" on the first sibling when none are selected
  const tabIndex = value === props.value ? 0 : -1;
  const isFirstSiblingAndNotSelected = !value && props.value === otherRadioValues[0];
  const finalTabIndex = isFirstSiblingAndNotSelected ? 0 : tabIndex;

  return (
    <div
      {...props}
      role="radio"
      tabIndex={finalTabIndex}
      aria-checked={value === props.value}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-radio-group-id={groupId}
      ref={ref}
    >
      {children}
    </div>
  );
};

export default Radio;
